import { supabase } from '../supabaseClient';
import { resolveForfeoReserveEdgeErrorCode } from './forfeoReserveEdgeError';
import {
  getForfeoReserveCalendarViewRange,
  validateForfeoReserveCalendarCommand,
  type ForfeoReserveCalendarCommand,
  type ForfeoReserveCalendarSnapshot,
  type ForfeoReserveCalendarView,
} from '../../supabase/functions/_shared/forfeo-reserve-calendar';

export const FORFEO_RESERVE_CALENDAR_FEATURE_FLAG =
  'VITE_FORFEO_RESERVE_CALENDAR_V1' as const;

export function isForfeoReserveCalendarEnabled(): boolean {
  return import.meta.env.VITE_FORFEO_RESERVE_CALENDAR_V1 === 'true';
}

export class ForfeoReserveCalendarClientError extends Error {
  readonly code: string;

  constructor(code: string) {
    super(code);
    this.name = 'ForfeoReserveCalendarClientError';
    this.code = code;
  }
}

function correlationId(): string {
  return `reserve-calendar:${crypto.randomUUID()}`;
}

export function createForfeoReserveCalendarCommand(
  command: Omit<ForfeoReserveCalendarCommand, 'correlationId' | 'idempotencyKey'>
    & Partial<Pick<ForfeoReserveCalendarCommand, 'correlationId' | 'idempotencyKey'>>,
): ForfeoReserveCalendarCommand {
  const complete: ForfeoReserveCalendarCommand = {
    ...command,
    correlationId: command.correlationId || correlationId(),
    idempotencyKey:
      command.idempotencyKey
      || `reserve:calendar:${crypto.randomUUID()}`,
  };
  const errors = validateForfeoReserveCalendarCommand(complete);
  if (errors.length > 0) {
    throw new ForfeoReserveCalendarClientError(errors.join(','));
  }
  return complete;
}

async function invokeCalendar<T>(
  body: Record<string, unknown>,
): Promise<T> {
  const { data, error } = await supabase.functions.invoke(
    'reserve-merchant-calendar',
    { body },
  );
  if (error) {
    throw new ForfeoReserveCalendarClientError(
      await resolveForfeoReserveEdgeErrorCode(
        data,
        error,
        'calendar_network_error',
      ),
    );
  }
  if (!data?.ok) {
    throw new ForfeoReserveCalendarClientError(
      String(data?.error || 'calendar_operation_failed'),
    );
  }
  return data as T;
}

export async function fetchForfeoReserveCalendarSnapshot(
  businessId: string,
  anchor: Date,
  view: ForfeoReserveCalendarView,
): Promise<ForfeoReserveCalendarSnapshot> {
  const range = getForfeoReserveCalendarViewRange(anchor, view);
  const data = await invokeCalendar<{
    ok: true;
    snapshot: ForfeoReserveCalendarSnapshot;
  }>({
    action: 'snapshot',
    businessId,
    ...range,
  });
  return data.snapshot;
}

export async function applyForfeoReserveCalendarCommand(
  command: ForfeoReserveCalendarCommand,
): Promise<Record<string, unknown>> {
  const errors = validateForfeoReserveCalendarCommand(command);
  if (errors.length > 0) {
    throw new ForfeoReserveCalendarClientError(errors.join(','));
  }
  const data = await invokeCalendar<{
    ok: true;
    result: Record<string, unknown>;
  }>({ action: 'command', command });
  return data.result;
}

export async function importForfeoReserveIcal(
  input: {
    businessId: string;
    calendarId: string;
    targetType: 'catalog_item' | 'resource';
    targetId: string;
    ical: string;
  },
): Promise<{ imported: number; conflicts: number; total: number }> {
  return invokeCalendar({
    action: 'import_ical',
    ...input,
    correlationId: correlationId(),
  });
}

export async function exportForfeoReserveIcal(
  input: {
    businessId: string;
    calendarId: string;
    startsAt: string;
    endsAt: string;
  },
): Promise<{ filename: string; ical: string }> {
  return invokeCalendar({ action: 'export_ical', ...input });
}
